import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { serverFetch } from "../../server";
import { Button, message, Table, Input, FloatButton, Spin, Select, Tag } from "antd";
import Client from "./client";
import { fmtDate } from "../../extensions/utils";
import { clientVids, getTableColumn, clientTypeFilter } from "../../extensions/helper";
import "./clients.css";
import { ExportExcelButton, HelpButton, isMobile, menuSize, PrintButton, RefreshButton, ScanButton } from "../../extensions/design";
import ClientItem from "./client-item";
import ClientFilter from "./client-filter";
import { Scan, AddFilter, FilterItem, SportTooltip } from "../../controls";
import ScanMobile from "../../controls/scan/scan-mobile";
import { BtnSettingsTable, tblGetColumns } from "../../controls/table-settings.js/table-settings";
import { exportExcel } from "../../extensions/export-excel";
import { dictsGetUsers } from "../../extensions/dicts";
import "../../controls/print-page/print-page.css";
import { PlusOutlined } from "@ant-design/icons";
import TriStateCheckbox from "../../controls/tri-state-checkbox";
import dayjs from "dayjs";

const tableName = "clients";
const SEARCH_DEBOUNCE_DELAY = 1000; // Задержка для дебаунса поиска в миллисекундах
const PAGE_SIZE = 50; // Размер страницы для бесконечного скролла

const clientListTagHex = (raw) => {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  return s.startsWith("#") ? s : `#${s}`;
};

const Clients = () => {
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [editId, setEditId] = useState(null);
  const [addingFilter, setAddingFilter] = useState(true);
  const [showFilter, setShowFilter] = useState(false);
  const [showScan, setShowScan] = useState(false);
  const [filter, setFilter] = useState({
    Types: [], // По всем клиентам на сегодня
    hasdeposit: null,
    hasdolg: null,
    DateVid: 0,
    DateBeg: dayjs().subtract(1, 'year'),
    DateEnd: dayjs(),
  });
  const [searchText, setSearchText] = useState("");
  const [userOptions, setUserOptions] = useState([]);
  const [managerOptions, setManagerOptions] = useState([]);
  const [tableKey, setTableKey] = useState(0);
  const [isPrinting, setIsPrinting] = useState(false);
  const searchTimeoutRef = useRef(null);
  const isLoadingRef = useRef(false);
  const [curClientName, setCurClientName] = useState("");

  const onFilterChange = (changedValues) => {
    setFilter(prev => ({ ...prev, ...changedValues }));
  };

  // Создание параметров запроса
  const buildParams = useCallback((offset, searchValue) => {
    console.log(filter)
    const params = new URLSearchParams();
    params.append("Offset", offset);
    params.append("Limit", PAGE_SIZE);
    console.log("UserID", filter.UserIds, "ManagerId", filter.ManagerIds)
    console.log("user?", filter.UserIds?.value?.length)
    if (searchValue) params.append("SearchText", searchValue);
    if (filter.Types?.length) filter.Types.forEach(v => params.append("Types", v));
    if (filter.UserIds?.value?.length) filter.UserIds.value.forEach(v => params.append("UserIds", v));
    if (filter.ManagerIds?.value?.length) filter.ManagerIds.value.forEach(v => params.append("ManagerIds", v));
    if (filter.hasdeposit != null) params.append("hasdeposit", filter.hasdeposit);
    if (filter.hasdolg != null) params.append("hasdolg", filter.hasdolg);
    
    return params;
  }, [filter]);

  // Функция загрузки данных с учетом поискового текста
  const loadDataWithSearch = useCallback((searchValue) => {
    if (isLoadingRef.current) return;
    
    isLoadingRef.current = true;
    setLoading(true);
    setItems([]);
    setOffset(0);
    setHasMore(true);

    serverFetch(`client?${buildParams(0, searchValue)}`)
      .then(data => {
        setItems(data);
        setHasMore(data.length >= PAGE_SIZE);
        setOffset(data.length);
      })
      .catch(e => message.error(e.userMessage))
      .finally(() => {
        setLoading(false);
        isLoadingRef.current = false;
      });
  }, [buildParams]);

  // Обработка изменения поискового текста с дебаунсом
  const onSearchChange = (text) => {
    setSearchText(text);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    
    searchTimeoutRef.current = setTimeout(() => {
      isLoadingRef.current = false;
      loadDataWithSearch(text);
    }, SEARCH_DEBOUNCE_DELAY);
  };

  const divRef = useRef(null);
  const tableScrollRef = useRef(null);

  // Инициализация и очистка
  useEffect(() => {
    dictsGetUsers(setUserOptions);
    dictsGetUsers(setManagerOptions);
    return () => searchTimeoutRef.current && clearTimeout(searchTimeoutRef.current);
  }, []);

  // Добавление обработчика скролла к таблице
  useEffect(() => {
    if (!isMobile && divRef.current) {
      const scrollContainer = divRef.current.querySelector('.ant-table-body');
      if (scrollContainer) {
        tableScrollRef.current = scrollContainer;
        scrollContainer.addEventListener('scroll', handleScroll);
        return () => scrollContainer.removeEventListener('scroll', handleScroll);
      }
    }
  }, [items, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Функция загрузки данных с поддержкой пагинации
  const getData = useCallback((currentOffset = 0, append = false) => {
    if (isLoadingRef.current) return;
    
    isLoadingRef.current = true;
    append ? setLoadingMore(true) : setLoading(true);

    serverFetch(`client?${buildParams(currentOffset, searchText)}`)
      .then(data => {
        setItems(prev => append ? [...prev, ...data] : data);
        setHasMore(data.length >= PAGE_SIZE);
        setOffset(currentOffset + data.length);
      })
      .catch(e => message.error(e.userMessage))
      .finally(() => {
        setLoading(false);
        setLoadingMore(false);
        isLoadingRef.current = false;
      });
  }, [buildParams, searchText]);

  // Загрузка данных при изменении фильтров
  useEffect(() => {
    isLoadingRef.current = false;
    setItems([]);
    setOffset(0);
    setHasMore(true);
    getData(0, false);
  }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Загрузка следующей порции данных
  const loadMore = useCallback(() => {
    if (!loadingMore && !loading && hasMore && !isLoadingRef.current) {
      getData(offset, true);
    }
  }, [loadingMore, loading, hasMore, offset, getData]);

  const onRenderVid = (value) => {
    const vid = clientVids.find((i) => i.value === value);
    return <img src={vid?.icon} alt={vid?.label} className="table-icon" />;
  };

  const filterOptions = [
    { type: "UserIds", label: "Тренер", tooltip: "Тренер клиента", inputType: "multiselect", options: userOptions },
    { type: "ManagerIds", label: "Менеджер", tooltip: "Менеджер клиента", inputType: "multiselect", options: managerOptions },
  ];

  const filterLabels = {
    UserIds: "Тренер",
    ManagerIds: "Менеджер",
  };

  const getUniqueOptions = (field) => {
    const unique = Array.from(new Set(items.map(item => item[field]).filter(Boolean)));
    return unique.map(value => ({ label: value, value }));
  };

  const handleAddTopFilter = (type) => {
    if (filter[type]) return;

    const option = filterOptions.find(opt => opt.type === type);
    const inputType = option?.inputType;
    let initialValue = undefined;
    let options = option?.options;

    if (inputType === "multiselect") {
      options = type === "UserIds" ? userOptions : type === "ManagerIds" ? managerOptions : getUniqueOptions(type);
      initialValue = [];
    }

    onFilterChange({ [type]: { value: initialValue, inputType, options }});
    setAddingFilter(false);
  };

  const renderFilterItem = (key, data) => {
    const option = filterOptions.find(f => f.type === key);
    return (
      <FilterItem
        key={key}
        type={option?.inputType}
        label={filterLabels[key] || key}
        value={data.value}
        options={option?.options}
        onChange={(val) => onFilterChange({ 
          [key]: { 
            ...data,
            value: val 
          }
        })}
        onRemove={() => {
          const { [key]: _, ...rest } = filter;
          setFilter(rest);
        }}
      />
    );
  };

  // Базовые колонки с дефолтными ширинами
  const baseColumns = [
    { dataIndex: "vid", render: onRenderVid, minWidth: 40, ellipsis: true, title: "Вид" },
    getTableColumn("id", "#", { type: "number", minWidth: 60, ellipsis: true }),
    getTableColumn("name", "Клиент", {
      isFilter: true,
      type: "string",
      minWidth: 340,
      ellipsis: true,
      render: (name, record) => (
        <div className="clients-name-tags-cell">
          <span className="clients-name-text">{name}</span>
          {(Array.isArray(record.tags) ? record.tags : []).map((t) => {
            const hex = clientListTagHex(t.color);
            return (
              <Tag
                key={t.id}
                className="clients-list-tag-chip"
                style={{
                  marginInlineEnd: 4,
                  marginTop: 2,
                  fontSize: 11,
                  lineHeight: "18px",
                  ...(hex ? { backgroundColor: hex, borderColor: hex, color: "#fff" } : {}),
                }}
              >
                {t.value ?? ""}
              </Tag>
            );
          })}
        </div>
      ),
    }),
    getTableColumn("card", <SportTooltip title="Код карты клиента" placement="top"><div><img src="/images/card.svg" className="table-header-img"/></div></SportTooltip>, { type: "string", minWidth: 120, ellipsis: true }),
    getTableColumn("dateBirth", <SportTooltip title="Дата рождения"><div>Дата р.</div></SportTooltip>, { isFilter: true, isSorter: true, type: "date", formatFn: fmtDate, minWidth: 100, ellipsis: true }),
    getTableColumn("dateReg", <SportTooltip title="Дата регистрации"><div>Дата рег.</div></SportTooltip>, { isFilter: true, isSorter: true, type: "date", formatFn: fmtDate, minWidth: 100, ellipsis: true }),
    getTableColumn("phone", "Телефон", { isSorter: true, type: "string", minWidth: 120, ellipsis: true }),
    getTableColumn("email", "Почта", { type: "string", minWidth: 200, ellipsis: true }),
    getTableColumn("userName", "Тренер", { type: "string", minWidth: 200 }),
    getTableColumn("managerName", "Менеджер", { type: "string", minWidth: 200, ellipsis: true }),
    getTableColumn("depos", "Депоз.", { isFilter: true, type: "number", minWidth: 80 }),
    getTableColumn("dolg", "Долг", { isFilter: true, type: "number", minWidth: 80 }),
  ];

  // Получаем колонки с учетом настроек видимости
  const columns = tblGetColumns(baseColumns, tableName);

  const filters = Object.entries(filter)
    .filter(([key]) => !["searchText", "Types", "hasdeposit", "hasdolg", "DateVid", "DateBeg", "DateEnd"].includes(key))
    .map(([type, data]) => ({ type, ...data }));

  const onCloseEdit = (refresh, newClientId) => {
    setEditId(null);
    if (refresh) {
      isLoadingRef.current = false; // Сбрасываем флаг перед обновлением
      loadDataWithSearch(searchText);
    }
    newClientId && setEditId(newClientId);
  };

  const onClientSaved = useCallback((client) => {
    if (!client?.id) return;
    setItems((prev) => prev.map((item) => (item.id === client.id ? { ...item, ...client } : item)));
    setCurClientName(client.name ?? "");
  }, []);

  const onScan = value => {
    setShowScan(false);
    if (!value || editId) return;
    
    const item = items.find(i => i.clientCard === value);
    if (item) {
      setEditId(item.id);
    } else {
      setLoading(true);
      serverFetch(`client/card/${value}`)
        .then(data => setEditId(data.id))
        .catch(() => setEditId(0))
        .finally(() => setLoading(false));
    }
  };

  const handlePrint = () => {
    setIsPrinting(true);
    setTimeout(() => {
      window.print();
      setIsPrinting(false);
    }, 500);
  };

  const handleExportExcel = () => {
    exportExcel(items, columns, "Клиенты.xlsx");
  };

  const onTableSettingsChange = () => setTableKey(prev => prev + 1);
  
  const getClientTypeHint = (value) => {
    switch (value) {
      case 0: return "Нет фильтра";
      case 1: return "Есть активированные абонементы";
      case 2: return "Есть неактивированные абонементы";
      case 3: return "Нет визитов по активированным абонементам за последние 30 дней";
      case 4: return "Есть более одного активированного абонемента, пакета";
      case 5: return "Нет проданных абонементов ";
      default: return "";
    }
  };
  const headerSize = divRef.current?.querySelector('.ant-table-thead')?.offsetHeight ?? 0;
  const tableHeight = divRef.current ? (isMobile ? (document.documentElement.clientHeight - headerSize - menuSize) :
    (document.documentElement.offsetHeight - headerSize - divRef.current.getBoundingClientRect().top - window.scrollY - 1)
  ) : 0;
  const tableWidth = (document.documentElement.offsetWidth - divRef.current?.getBoundingClientRect().left) ?? 0;

  const divTools = isMobile ? 
    <></> :
    <>
      <div className="sport-form-title" style={{paddingLeft: 0}}>
        <Input placeholder="Поиск" className="search-str" value={searchText} onChange={(e) => onSearchChange(e.target.value)} allowClear style={{maxWidth: 400}}/>
        <ScanButton
          title="Просканируйте карту клиента"
          onScan={onScan}
          id="tour-item-clients-scan-button"
          style={{ marginLeft: 0 }}
        />
        <Button onClick={() => setEditId(0)} className="search-btn">Новый</Button>
        <RefreshButton onClick={() => { isLoadingRef.current = false; loadDataWithSearch(searchText); }} />
        <PrintButton onClick={handlePrint} />
        <ExportExcelButton onClick={handleExportExcel} />
        <BtnSettingsTable name={tableName} columns={baseColumns} onSettingsChange={onTableSettingsChange}/>
        <HelpButton article="Clients"/>
      </div>
      <div className="sport-form-title" style={{paddingTop: "0px", display: "grid", gridTemplateColumns:"1fr 600px"}}>      
        <div className="filter-container" style={{gridTemplateColumns:"1fr 1fr"}}>
          {filters.length === 0 && !addingFilter && (<AddFilter availableFilters={filterOptions} usedFilters={filters.map(f => f.type)} onAdd={handleAddTopFilter}/>)}
          {filters.map((f) => renderFilterItem(f.type, f))}
          {filters.length < filterOptions.length && (
            addingFilter ? (<AddFilter availableFilters={filterOptions} usedFilters={filters.map(f => f.type)} onAdd={handleAddTopFilter}/>) : (
              filters.length !== 0 && (<Button onClick={() => setAddingFilter(true)} type="dashed" className="filter-add-button">+</Button>)
            )
          )}
        </div>
          <div className="static-filter-div" style={{gridTemplateColumns:"350px 140px 105px"}}>
            <div>
            <span>По всем клиентам</span>
              <Select
              style={{paddingLeft: "4px"}}
                value={filter.Types}
                mode="multiple"
                onChange={(val) => onFilterChange({ Types: val })}
                maxTagCount={1}
                maxTagPlaceholder={filter.Types?.length > 1 ? `+${filter.Types.length - 1}` : ''}
                options={clientTypeFilter.map((opt) => ({
                  ...opt,
                  label: (
                    <SportTooltip title={getClientTypeHint(opt.value)} placement="right">
                      {opt.label}
                    </SportTooltip>
                  ),
                }))}
              />
            </div>
                
            <TriStateCheckbox value={filter.hasdeposit} onChange={(val) => onFilterChange({ hasdeposit: val })}>С Депозитом</TriStateCheckbox>
            <TriStateCheckbox value={filter.hasdolg} onChange={(val) => onFilterChange({ hasdolg: val })}>С Долгом</TriStateCheckbox>
          </div>
      </div>
    </>
  
  // Обработчик скролла
  const handleScroll = (e, threshold = 100) => {
    const target = e.target || e.currentTarget;
    if (target.scrollHeight - target.scrollTop <= target.clientHeight + threshold) {
      loadMore();
    }
  };

  const divTable = isMobile ? 
  <div style={{ position: "relative", height: window.innerHeight - 45.8 }}>
    <div className="col-one-title-upper-menu">
      <div className="rounded-search-field" style={{padding: "0px", height: "35px"}}>
        <Input placeholder="Поиск" value={searchText} onChange={(e) => onSearchChange(e.target.value)} allowClear className="search-str" style={{ paddingLeft: "16px", borderRadius:"100px", width: "100%", minHeight: "35px" }}/>
      </div>
      <div className="rounded-filter-button" onClick={() => setShowFilter(true)}>
        <img src="/images/filter.svg" className="mobile-top-img" alt="filter" style={{ width: "20px", height: "20px" }} />
      </div>
    </div>

    <div className="client-items" style={{height: "100%", overflowY: "scroll", overflowX: "clip", paddingTop: "55px"}} onScroll={(e) => handleScroll(e, 50)}>
      {items.map(item => <ClientItem item={item} key={item.id} onClick={() => {setEditId(item.id); setCurClientName(item.name)}}/> )}
      {loadingMore && <div style={{textAlign: 'center', padding: '20px'}}><Spin /></div>}
    </div>
  </div> : 
  <div ref={divRef} style={{ width: tableWidth}} className="col1-col">
    <div className="print-area" style={{ height: "100%", width: "100%" }}>
      <Table key={tableKey} dataSource={items} loading={loading} columns={columns} size="small" pagination={false} scroll={isPrinting ? undefined : { y: tableHeight - 40 }} 
        sticky={!isPrinting} tableLayout="auto" rowSelection={{ type: 'radio', selectedRowKeys: [editId], renderCell: checked => <div className={checked ? "list-indicator" : ""} />}}
        rowKey="id" onRow={(record) => ({ onClick: () => setEditId(record.id) })}
      />
      {loadingMore && (<div style={{textAlign: 'center', padding: '10px', position: 'absolute', bottom: '10px', left: '50%', transform: 'translateX(-50%)'}}><Spin /></div>)}
    </div> 
  </div>

  return (
    <div className="sport-form-content" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {(isMobile || editId != null) ? null : <Scan onScan={onScan}/>}
      {divTools}
      {divTable}
      {isMobile && editId == null && <>
        <FloatButton.Group trigger="click" icon={<PlusOutlined />}>
          <FloatButton style={{ insetBlockEnd: 196 }}  icon={<img src="/images/scan.svg" alt="scan" style={{width : 24}}/>} onClick={() => setShowScan(true)} />
          {editId == null ? <FloatButton style={{ insetBlockEnd: 108 }}  icon={<img src="/images/user_plus_outline.svg" alt="client" style={{width : 24}}/>} onClick={() => setEditId(0)} /> : null}
        </FloatButton.Group>
      </>}
      {editId != null && <Client name={curClientName} id={editId} onClose={onCloseEdit} onSaved={onClientSaved} />}
      {showFilter && <ClientFilter current={filter} filterOptions={filterOptions} clientTypeFilter={clientTypeFilter} onClose={(newFilter) => {
        setShowFilter(false);
        if (newFilter) { setFilter(newFilter); }
      }}
    />}
    {(editId == null) && showScan && <ScanMobile onScan={onScan} onClose={() => setShowScan(false)} />} 
    </div>
  );
};

export default Clients;
